import posthog from 'posthog-js'
import { useState } from 'react'
import { FEEDBACK_SURVEY } from '../constants'
import { sleep } from '../utilities/general'
import ToggleListItem from './ToggleListItem'
import DialogContent from '@mui/material/DialogContent'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import List from '@mui/material/List'
import Modal from './Modal'
import { useModalClose } from '../hooks/useModalClose'

type Props = {
  inline?: boolean
}

export default function ModalFeedback({ inline }: Readonly<Props>) {
  const { open, handleClose } = useModalClose('/', inline)
  const [message, setMessage] = useState('')
  const [wantsReply, setWantsReply] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = message.trim()
    if (!trimmed) {
      setError('Please add a quick note so we know what to improve.')
      return
    }

    setSubmitting(true)
    setError('')

    posthog.capture('survey sent', {
      $survey_id: FEEDBACK_SURVEY.id,
      $survey_name: FEEDBACK_SURVEY.name,
      $survey_type: FEEDBACK_SURVEY.type,
      $survey_questions: [
        {
          id: FEEDBACK_SURVEY.questions.message,
          question: 'Feedback',
          type: 'open',
        },
        {
          id: FEEDBACK_SURVEY.questions.reply,
          question: "I'd like a reply",
          type: 'single_choice',
          choices: ['Yes', 'No'],
        },
      ],
      [`$survey_response_${FEEDBACK_SURVEY.questions.message}`]: trimmed,
      [`$survey_response_${FEEDBACK_SURVEY.questions.reply}`]: wantsReply ? 'Yes' : 'No',
    })

    setSent(true)
    setMessage('')
    setWantsReply(false)
    setSubmitting(false)
    await sleep(800)
    await handleClose()
  }

  return (
    <Modal open={open} inline={inline} maxWidth="sm" onClose={inline ? undefined : handleClose} color="primary.main">
      <DialogContent>
        <Stack component="form" onSubmit={handleSubmit} sx={{
          gap: 3
        }}>
          <Typography variant="subtitle1" color="primary" sx={{
            flexGrow: 1
          }}>
            Tell us what you think!
          </Typography>
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            Quick thoughts, blockers, or wishes. This goes straight to the team.
          </Typography>
          {error && <Alert severity="warning">{error}</Alert>}
          {sent && <Alert severity="success">Thanks for the feedback!</Alert>}

          <Stack>
            <TextField
              autoFocus
              multiline
              label="Feedback"
              placeholder="What are you trying to do? What's missing?"
              value={message}
              onChange={event => setMessage(event.target.value)}
              slotProps={{ input: { inputProps: { maxLength: 1000 } } }}
              disabled={submitting}
              minRows={4}
            />
            <List disablePadding sx={{ marginTop: 0 }}>
              <ToggleListItem checked={wantsReply} onClick={checked => setWantsReply(!!checked)} disabled={submitting}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    flexGrow: 1
                  }}>
                  I'd like a reply
                </Typography>
              </ToggleListItem>
            </List>
            <Stack direction="row" spacing={1} sx={{
              justifyContent: 'flex-end'
            }}>
              <Button onClick={handleClose} disabled={submitting} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Modal>
  )
}
